// Sons originaux synthétisés en WebAudio (aucun fichier externe).
import { getSettings } from './settings';

type Sfx =
  | 'shoot'
  | 'hit'
  | 'break'
  | 'level'
  | 'death'
  | 'evolve'
  | 'kill'
  | 'notify'
  | 'alert'
  | 'uiHover'
  | 'uiClick'
  | 'uiNav'
  | 'uiBuy'
  | 'uiDeny'
  | 'uiReward'
  | 'uiFire';

/** Sons d'interface (progression, messages, menus) : réglés à part des effets de combat. */
const UI_SOUNDS = new Set<Sfx>(['level', 'evolve', 'notify', 'uiHover', 'uiClick', 'uiNav', 'uiBuy', 'uiDeny', 'uiReward', 'uiFire']);

class Audio {
  private ctx: AudioContext | null = null;
  private master: GainNode | null = null;
  private noise: AudioBuffer | null = null;
  private last = new Map<Sfx, number>();

  unlock(): void {
    if (this.ctx) {
      if (this.ctx.state === 'suspended') void this.ctx.resume();
      return;
    }
    try {
      this.ctx = new AudioContext();
      this.master = this.ctx.createGain();
      this.master.connect(this.ctx.destination);
      const len = this.ctx.sampleRate * 0.4;
      this.noise = this.ctx.createBuffer(1, len, this.ctx.sampleRate);
      const d = this.noise.getChannelData(0);
      for (let i = 0; i < len; i++) d[i] = Math.random() * 2 - 1;
    } catch {
      this.ctx = null;
    }
  }

  play(kind: Sfx, gain = 1): void {
    const ctx = this.ctx;
    const master = this.master;
    if (!ctx || !master || ctx.state !== 'running') return;
    const s = getSettings();
    if (s.muteHidden && typeof document !== 'undefined' && document.hidden) return;
    const vol = s.volume * (UI_SOUNDS.has(kind) ? s.uiVolume : s.sfxVolume);
    if (vol <= 0) return;
    const now = ctx.currentTime;
    // Limite le nombre de sons identiques par seconde pour éviter la saturation.
    const minGap = kind === 'shoot' ? 0.045 : kind === 'hit' ? 0.03 : kind === 'uiHover' ? 0.06 : 0.08;
    if (now - (this.last.get(kind) ?? -1) < minGap) return;
    this.last.set(kind, now);
    master.gain.value = vol * 0.5;
    const g = ctx.createGain();
    g.connect(master);
    const env = (peak: number, attack: number, decay: number) => {
      g.gain.setValueAtTime(0.0001, now);
      g.gain.exponentialRampToValueAtTime(Math.max(0.0002, peak * gain), now + attack);
      g.gain.exponentialRampToValueAtTime(0.0001, now + attack + decay);
    };
    const osc = (type: OscillatorType, f0: number, f1: number, dur: number) => {
      const o = ctx.createOscillator();
      o.type = type;
      o.frequency.setValueAtTime(f0, now);
      o.frequency.exponentialRampToValueAtTime(Math.max(20, f1), now + dur);
      o.connect(g);
      o.start(now);
      o.stop(now + dur + 0.02);
    };
    const noise = (dur: number, freq: number, q = 1) => {
      if (!this.noise) return;
      const src = ctx.createBufferSource();
      src.buffer = this.noise;
      const f = ctx.createBiquadFilter();
      f.type = 'bandpass';
      f.frequency.value = freq;
      f.Q.value = q;
      src.connect(f);
      f.connect(g);
      src.start(now);
      src.stop(now + dur);
    };
    switch (kind) {
      case 'shoot':
        env(0.22, 0.004, 0.09);
        noise(0.1, 1800, 0.8);
        osc('square', 220, 90, 0.07);
        break;
      case 'hit':
        env(0.12, 0.002, 0.05);
        noise(0.06, 3200, 2);
        break;
      case 'break':
        env(0.28, 0.003, 0.22);
        noise(0.25, 900, 0.7);
        osc('triangle', 300, 70, 0.2);
        break;
      case 'kill':
        env(0.35, 0.005, 0.4);
        noise(0.4, 500, 0.6);
        osc('sawtooth', 160, 40, 0.38);
        break;
      case 'death':
        env(0.4, 0.01, 0.8);
        noise(0.4, 300, 0.5);
        osc('sawtooth', 120, 30, 0.8);
        break;
      case 'level':
        env(0.25, 0.01, 0.35);
        osc('triangle', 440, 880, 0.3);
        break;
      case 'evolve':
        env(0.3, 0.02, 0.6);
        osc('triangle', 330, 990, 0.55);
        osc('sine', 495, 1485, 0.55);
        break;
      case 'notify':
        env(0.16, 0.005, 0.18);
        osc('sine', 660, 990, 0.16);
        break;
      case 'alert':
        env(0.24, 0.01, 0.45);
        osc('triangle', 620, 410, 0.42);
        osc('square', 310, 205, 0.42);
        break;
      // Menus : cliquetis métalliques courts, jamais plus forts qu'un tir.
      case 'uiHover':
        env(0.035, 0.002, 0.03);
        noise(0.035, 5200, 6);
        break;
      case 'uiClick':
        env(0.1, 0.002, 0.06);
        noise(0.05, 2600, 3);
        osc('square', 180, 120, 0.04);
        break;
      case 'uiNav':
        env(0.09, 0.004, 0.12);
        osc('triangle', 520, 700, 0.1);
        noise(0.04, 4000, 4);
        break;
      case 'uiBuy':
        env(0.2, 0.004, 0.4);
        osc('triangle', 660, 660, 0.12);
        osc('sine', 990, 1320, 0.36);
        noise(0.08, 3000, 2);
        break;
      case 'uiDeny':
        env(0.14, 0.004, 0.18);
        osc('square', 160, 110, 0.16);
        break;
      case 'uiReward':
        env(0.22, 0.01, 0.7);
        osc('triangle', 392, 784, 0.5);
        osc('sine', 588, 1176, 0.6);
        break;
      case 'uiFire':
        env(0.12, 0.003, 0.08);
        noise(0.08, 1600, 0.9);
        osc('square', 200, 90, 0.06);
        break;
    }
  }
}

export const audio = new Audio();
